import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, MapPin, Archive, RefreshCcw, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", aliases: "", is_active: true };

export default function Regions() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin" || me?.role_name === "Admin";
  const [regions, setRegions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/regions?include_archived=true");
      setRegions(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name || "", aliases: (r.aliases || []).join(", "), is_active: r.is_active !== false });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Region name is required"); return; }
    const payload = {
      name: form.name.trim(),
      aliases: form.aliases.split(",").map((a) => a.trim()).filter(Boolean),
      is_active: !!form.is_active,
    };
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/regions/${editing.id}`, payload);
        toast.success("Region updated");
      } else {
        await api.post("/regions", payload);
        toast.success("Region added");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive region '${r.name}'? Existing records keep their region tag but this hub won't show in create forms.`)) return;
    try {
      await api.delete(`/regions/${r.id}`);
      toast.success("Region archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (r) => {
    try {
      await api.post(`/regions/${r.id}/restore`);
      toast.success("Region restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="regions-forbidden">
        Only admins can manage regions.
      </div>
    );
  }

  return (
    <div data-testid="regions-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Masters</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Regions / Hubs</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">
            Physical branch geographies. Used for slicing Leads, Quotations and Demo bookings.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-region-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add region
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit region" : "Add region"}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {editing ? "Rename or update aliases. Cascade will propagate the new name to linked leads, quotes and demos." : "Physical branch that will show up in create forms across Leads, Quotations and Demo Bookings."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="region-name-input" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Aliases (comma-separated)</Label>
                <Input
                  value={form.aliases}
                  onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                  placeholder="e.g. gurgaon, noida, ncr"
                  data-testid="region-aliases-input"
                  className="mt-1"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  City / metro names used to auto-suggest this region when a new lead's city matches.
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input
                  id="region-active"
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="region-active-toggle"
                />
                <label htmlFor="region-active" className="text-slate-700">Active (shown in create forms)</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="region-save-btn">
                  {editing ? "Save changes" : "Add region"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Region</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Aliases</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-40 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regions.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-10">No regions yet.</TableCell></TableRow>
              ) : regions.map((r) => (
                <TableRow key={r.id} data-testid={`region-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-900 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-sky-600" /> {r.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{(r.aliases || []).join(", ") || "—"}</TableCell>
                  <TableCell>
                    {r.is_active === false
                      ? <Badge variant="secondary" className="bg-slate-100 text-slate-600">Archived</Badge>
                      : <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">Active</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`region-edit-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {r.is_active === false ? (
                        <Button size="sm" variant="outline" onClick={() => restore(r)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`region-restore-${r.id}`}>
                          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Restore
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => archive(r)} className="text-slate-600 border-slate-200 hover:bg-slate-50" data-testid={`region-archive-${r.id}`}>
                          <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Mobile */}
        <ul className="md:hidden divide-y divide-slate-100">
          {regions.length === 0 ? (
            <li className="text-center text-slate-400 py-10">No regions yet.</li>
          ) : regions.map((r) => (
            <li key={r.id} className="p-4" data-testid={`region-card-m-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-sky-600" /> {r.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{(r.aliases || []).join(", ") || "—"}</div>
                </div>
                {r.is_active === false
                  ? <Badge variant="secondary" className="bg-slate-100 text-slate-600 shrink-0">Archived</Badge>
                  : <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 shrink-0">Active</Badge>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(r)}>Edit</Button>
                {r.is_active === false
                  ? <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" onClick={() => restore(r)}>Restore</Button>
                  : <Button size="sm" variant="outline" className="text-slate-600" onClick={() => archive(r)}>Archive</Button>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
